import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  // Questionnaire templates
  templates: [],
  templatesLoading: false,
  templatesError: null,

  // Questionnaire responses
  responses: [],
  responsesLoading: false,
  responsesError: null,

  // Pagination
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  },

  // Search and filters
  searchTerm: "",
  filters: {},

  // Form states
  createFormLoading: false,
  updateFormLoading: false,
  deleteLoading: false,

  // Success messages
  successMessage: null,
  errorMessage: null,
};

const questionnaireSlice = createSlice({
  name: "questionnaire",
  initialState,
  reducers: {
    // ==================== TEMPLATES ====================

    // Get templates
    getTemplatesStart: (state) => {
      state.templatesLoading = true;
      state.templatesError = null;
    },
    getTemplatesSuccess: (state, action) => {
      state.templatesLoading = false;
      state.templates = action.payload.templates;
      state.pagination = action.payload.pagination;
    },
    getTemplatesFailure: (state, action) => {
      state.templatesLoading = false;
      state.templatesError = action.payload;
    },

    // Create template
    createTemplateStart: (state) => {
      state.createFormLoading = true;
      state.errorMessage = null;
    },
    createTemplateSuccess: (state, action) => {
      state.createFormLoading = false;
      state.templates.unshift(action.payload);
      state.successMessage = "Questionnaire template created successfully";
      state.pagination.totalItems += 1;
    },
    createTemplateFailure: (state, action) => {
      state.createFormLoading = false;
      state.errorMessage = action.payload;
    },

    // Update template
    updateTemplateStart: (state) => {
      state.updateFormLoading = true;
      state.errorMessage = null;
    },
    updateTemplateSuccess: (state, action) => {
      state.updateFormLoading = false;
      const index = state.templates.findIndex(
        (t) => t._id === action.payload._id
      );
      if (index !== -1) {
        state.templates[index] = action.payload;
      }
      state.successMessage = "Questionnaire template updated successfully";
    },
    updateTemplateFailure: (state, action) => {
      state.updateFormLoading = false;
      state.errorMessage = action.payload;
    },

    // Delete template
    deleteTemplateStart: (state) => {
      state.deleteLoading = true;
      state.errorMessage = null;
    },
    deleteTemplateSuccess: (state, action) => {
      state.deleteLoading = false;
      state.templates = state.templates.filter(
        (t) => t._id !== action.payload._id
      );
      state.successMessage = "Questionnaire template deleted successfully";
      state.pagination.totalItems -= 1;
    },
    deleteTemplateFailure: (state, action) => {
      state.deleteLoading = false;
      state.errorMessage = action.payload;
    },

    // ==================== RESPONSES ====================

    // Get responses
    getResponsesStart: (state) => {
      state.responsesLoading = true;
      state.responsesError = null;
    },
    getResponsesSuccess: (state, action) => {
      state.responsesLoading = false;
      state.responses = action.payload.responses;
      state.pagination = action.payload.pagination;
    },
    getResponsesFailure: (state, action) => {
      state.responsesLoading = false;
      state.responsesError = action.payload;
    },

    // ==================== UTILITIES ====================

    // Set search term
    setSearchTerm: (state, action) => {
      state.searchTerm = action.payload;
    },

    // Set filters
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },

    // Clear filters
    clearFilters: (state) => {
      state.filters = {};
    },

    // Set pagination
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },

    // Clear messages
    clearMessages: (state) => {
      state.successMessage = null;
      state.errorMessage = null;
    },

    // Reset state
    resetQuestionnaireState: (state) => {
      return initialState;
    },
  },
});

export const {
  // Templates
  getTemplatesStart,
  getTemplatesSuccess,
  getTemplatesFailure,
  createTemplateStart,
  createTemplateSuccess,
  createTemplateFailure,
  updateTemplateStart,
  updateTemplateSuccess,
  updateTemplateFailure,
  deleteTemplateStart,
  deleteTemplateSuccess,
  deleteTemplateFailure,

  // Responses
  getResponsesStart,
  getResponsesSuccess,
  getResponsesFailure,

  // Utilities
  setSearchTerm,
  setFilters,
  clearFilters,
  setPagination,
  clearMessages,
  resetQuestionnaireState,
} = questionnaireSlice.actions;

export default questionnaireSlice.reducer;
